import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { GroupedList, GroupedListRow } from "@/components/ui/GroupedList";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, CreditCard, FileEdit, Download, Upload, Users, Bell, ShieldCheck, Camera, QrCode, GraduationCap, Calendar, Zap, Shield } from "lucide-react";
import { requestNotificationPermission, showLocalNotification } from "@/lib/pwa";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [exports, setExports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api.get("/exports/history").then((r) => setExports(r.data)).catch(() => {});
    api.get("/admin/stats").then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target.result; };
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const max = 256;
        const scale = Math.min(max / img.width, max / img.height, 1);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        try {
          await api.put("/users/me", { picture: dataUrl });
          await refresh();
          toast.success("Photo mise à jour");
        } catch { toast.error("Erreur upload"); }
        finally { setUploading(false); }
      };
      img.onerror = () => { setUploading(false); toast.error("Image illisible"); };
      reader.readAsDataURL(file);
    } catch { setUploading(false); toast.error("Erreur"); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <AppShell>
      {/* Header */}
      <div className="px-5 pt-6 pb-2">
        <div className="hp-label">Compte</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[var(--text)] tracking-tight leading-[0.98] mt-1" data-testid="settings-title">Mon profil</h1>
      </div>

      {/* Profile summary card */}
      <div className="px-5 mt-6">
        <div className="hp-card p-5 relative overflow-hidden">
          <div className="brand-glow absolute inset-0 pointer-events-none opacity-80" />
          <div className="flex items-center gap-4 relative">
            <label className="relative w-16 h-16 rounded-xl bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center text-[var(--text)] text-2xl font-semibold cursor-pointer overflow-hidden group shrink-0" data-testid="photo-upload-label">
              {user?.picture ? (
                <img src={user.picture} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-emerald-300">{user?.name?.charAt(0).toUpperCase() || "?"}</span>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                <Camera className="w-5 h-5 text-white" />
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={uploading} data-testid="photo-input" />
            </label>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-lg truncate text-[var(--text)]">{user?.name}</div>
              <div className="text-sm text-zinc-500 truncate">{user?.email}</div>
              <div className="mt-2 inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
                Plan {user?.plan}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Entreprise & Business */}
      <div className="px-5 mt-6">
        <GroupedList title="Entreprise">
          <GroupedListRow icon={FileEdit} label="Profil entreprise" sublabel="Secteur, zone, ICP…" onClick={() => navigate("/onboarding")} testId="edit-profile" />
          <GroupedListRow icon={CreditCard} label="Abonnement" sublabel={`Plan ${user?.plan || "Indépendant"}`} onClick={() => navigate("/pricing")} testId="manage-plan" />
          <GroupedListRow icon={Users} label="Équipe & rapport N+1" onClick={() => navigate("/team")} testId="go-team" />
          <GroupedListRow icon={QrCode} label="Ma carte de visite" sublabel="QR code & lien public" onClick={() => navigate("/card")} testId="go-card" />
        </GroupedList>
      </div>

      {/* Productivité */}
      <div className="px-5 mt-5">
        <GroupedList title="Productivité">
          <GroupedListRow icon={Zap} label="Envoi automatique d'emails" sublabel="Beta · Relances après visites" onClick={() => navigate("/emails/settings")} testId="go-email-settings" />
          <GroupedListRow icon={Upload} label="Importer des leads" onClick={() => navigate("/import")} testId="go-import" />
          <GroupedListRow icon={Calendar} label="Synchroniser mon calendrier" sublabel="iPhone · Google · Outlook" onClick={() => navigate("/calendar")} testId="go-calendar" />
          <GroupedListRow
            icon={Bell}
            label="Activer les notifications"
            testId="enable-notifications"
            onClick={async () => {
              const p = await requestNotificationPermission();
              if (p === "granted") {
                await showLocalNotification("Prosperr", "Les notifications sont activées");
                toast.success("Notifications activées");
              } else if (p === "denied") {
                toast.error("Notifications refusées. Autorisez-les dans les paramètres du navigateur.");
              } else if (p === "unsupported") {
                toast.error("Navigateur non compatible");
              }
            }}
          />
          <GroupedListRow icon={GraduationCap} label="Prosperr Academy" sublabel="Cours vidéo & certifications" onClick={() => navigate("/courses")} testId="go-courses" />
        </GroupedList>
      </div>

      {/* Confidentialité */}
      <div className="px-5 mt-5">
        <GroupedList title="Confidentialité">
          <GroupedListRow icon={ShieldCheck} label="Sécurité & 2FA" sublabel="Double authentification, codes de secours" onClick={() => navigate("/security")} testId="go-security" />
          <GroupedListRow icon={ShieldCheck} label="Vie privée & RGPD" sublabel="Export, suppression compte" onClick={() => navigate("/rgpd")} testId="go-rgpd" />
        </GroupedList>
      </div>

      {/* Exports history */}
      {exports.length > 0 && (
        <div className="px-5 mt-5">
          <GroupedList title="Historique des exports">
            {exports.map((e, i) => (
              <GroupedListRow
                key={i}
                icon={Download}
                label={`${e.count} leads · ${e.format.toUpperCase()}`}
                sublabel={new Date(e.date).toLocaleString("fr-FR")}
                right={<span />}
              />
            ))}
          </GroupedList>
        </div>
      )}

      {/* Admin console */}
      {isAdmin && (
        <div className="px-5 mt-6">
          <GroupedList title="Console admin">
            <GroupedListRow
              icon={Shield}
              label="Panneau administration"
              sublabel="Utilisateurs, rôles, modules, backups…"
              onClick={() => navigate("/prosperrboss")}
              testId="link-admin-panel"
            />
            <GroupedListRow
              icon={Zap}
              label="Insights onboarding"
              sublabel="Plan d'auto-amélioration Prosperr"
              onClick={() => navigate("/admin/onboarding/insights")}
              testId="link-admin-insights"
            />
            <GroupedListRow
              icon={ShieldCheck}
              label="Erreurs frontend"
              sublabel="Monitoring production"
              onClick={() => navigate("/admin/frontend/errors")}
              testId="link-admin-errors"
            />
          </GroupedList>
        </div>
      )}

      {/* Logout */}
      <div className="px-5 mt-8 pb-2">
        <button onClick={doLogout} className="hp-btn hp-btn-secondary w-full h-12" data-testid="logout-btn">
          <LogOut className="w-4 h-4" />Se déconnecter
        </button>
      </div>
    </AppShell>
  );
}
